import { Directive, inject, NgZone, OnDestroy, output } from '@angular/core';

/** Second tap must land within this to read as a double tap — the platform double-click window. */
export const POINTER_DOUBLE_TAP_MS = 300;
/** Hold this long without moving and it is a long press, not a tap. */
export const POINTER_LONG_PRESS_MS = 500;
/**
 * Slop in CSS pixels. A finger never holds perfectly still, and a horizontal drag to scroll a kanban
 * starts exactly like a tap — beyond this the gesture is a drag and no tap is ever emitted.
 */
export const POINTER_MOVE_TOLERANCE_PX = 8;

/** What a gesture reports: the originating event plus where it landed. */
export interface PointerGestureEvent {
  readonly event: PointerEvent;
  readonly x: number;
  readonly y: number;
  /** True for touch and pen, so a consumer can treat a long press as the touch stand-in for open. */
  readonly touch: boolean;
}

interface ActiveGesture {
  pointerId: number;
  startX: number;
  startY: number;
  touch: boolean;
  longPressed: boolean;
  timer: ReturnType<typeof setTimeout> | null;
}

/**
 * Tap / double-tap / long-press on one element, for touch and mouse alike.
 *
 * iPad customers have no hover and no dblclick they can reach reliably: a card that only listens for
 * `click` + `dblclick` gives them selection and no way to open. Pointer Events unify the two input
 * kinds, so the same host reports a tap for a click, a doubleTap for a double click, and a longPress
 * that touch users reach instead of a double tap.
 *
 * Movement is what separates a tap from a scroll. The board scrolls horizontally under the cards, so
 * any pointer that travels past the tolerance cancels its gesture outright rather than firing a tap
 * the operator never meant.
 */
@Directive({
  selector: '[wbPointerGestures]',
  standalone: true,
  host: {
    '(pointerdown)': 'onPointerDown($event)',
    '(pointerup)': 'onPointerUp($event)',
    '(pointermove)': 'onPointerMove($event)',
    '(pointercancel)': 'onPointerCancel($event)',
    '(contextmenu)': 'onContextMenu($event)',
    // A long press on iOS otherwise raises the callout menu over the card; `manipulation` also drops
    // the 300ms click delay that made every tap feel late.
    '[style.touch-action]': '"manipulation"',
    '[style.-webkit-touch-callout]': '"none"',
    '[style.user-select]': '"none"',
  },
})
export class PointerGesturesDirective implements OnDestroy {

  private readonly zone = inject(NgZone);

  public readonly tap = output<PointerGestureEvent>();
  public readonly doubleTap = output<PointerGestureEvent>();
  public readonly longPress = output<PointerGestureEvent>();

  private active: ActiveGesture | null = null;
  /** When the last completed tap landed, so the next one can be judged a double. */
  private lastTapAt = 0;
  private lastTapX = 0;
  private lastTapY = 0;

  protected onPointerDown(event: PointerEvent): void {

    // Only the primary button opens a gesture; a right-click is the context menu's, not ours.
    if (event.button != null && event.button !== 0) return;

    this.clearTimer();

    const touch = event.pointerType !== 'mouse';
    const gesture: ActiveGesture = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      touch,
      longPressed: false,
      timer: null,
    };

    this.active = gesture;

    // Outside the zone so a gesture that is never completed costs no change detection at all.
    this.zone.runOutsideAngular(() => {
      gesture.timer = setTimeout(() => {
        if (this.active !== gesture) return;
        gesture.longPressed = true;
        gesture.timer = null;
        // A long press is terminal: it consumes the gesture so the pointerup emits no tap.
        this.zone.run(() => this.longPress.emit(this.toGestureEvent(event, gesture)));
      }, POINTER_LONG_PRESS_MS);
    });
  }

  protected onPointerMove(event: PointerEvent): void {

    const gesture = this.active;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    if (this.withinTolerance(gesture.startX, gesture.startY, event.clientX, event.clientY)) return;

    // Past the slop this pointer is driving a scroll or a drag — abandon it without emitting.
    this.cancel();
  }

  protected onPointerUp(event: PointerEvent): void {

    const gesture = this.active;
    if (!gesture || gesture.pointerId !== event.pointerId) return;

    this.clearTimer();
    this.active = null;

    if (gesture.longPressed) return;
    if (!this.withinTolerance(gesture.startX, gesture.startY, event.clientX, event.clientY)) return;

    const now = Date.now();
    const isDouble = now - this.lastTapAt <= POINTER_DOUBLE_TAP_MS
      && this.withinTolerance(this.lastTapX, this.lastTapY, event.clientX, event.clientY);

    if (isDouble) {
      // The pair is spent — a third tap starts a fresh single, never a second double.
      this.lastTapAt = 0;
      this.doubleTap.emit(this.toGestureEvent(event, gesture));
      return;
    }

    this.lastTapAt = now;
    this.lastTapX = event.clientX;
    this.lastTapY = event.clientY;
    this.tap.emit(this.toGestureEvent(event, gesture));
  }

  protected onPointerCancel(event: PointerEvent): void {

    if (this.active && this.active.pointerId !== event.pointerId) return;

    this.cancel();
  }

  /** A long press on touch would otherwise also raise the browser's own menu over the card. */
  protected onContextMenu(event: MouseEvent): void {

    if (this.active?.touch) event.preventDefault();
  }

  public ngOnDestroy(): void {

    this.clearTimer();
    this.active = null;
  }

  private cancel(): void {

    this.clearTimer();
    this.active = null;
  }

  private clearTimer(): void {

    if (this.active?.timer != null) clearTimeout(this.active.timer);
    if (this.active) this.active.timer = null;
  }

  private withinTolerance(fromX: number, fromY: number, toX: number, toY: number): boolean {

    return Math.abs(toX - fromX) <= POINTER_MOVE_TOLERANCE_PX && Math.abs(toY - fromY) <= POINTER_MOVE_TOLERANCE_PX;
  }

  private toGestureEvent(event: PointerEvent, gesture: ActiveGesture): PointerGestureEvent {

    return { event, x: event.clientX, y: event.clientY, touch: gesture.touch };
  }
}
